import React, { useEffect, useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useForm, Controller } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { noop } from 'lodash'
import { useSelector, useDispatch } from 'react-redux'
import { Stack, Typography, IconButton, Divider, Button, Autocomplete, TextField } from '@mui/material_modern'
import { OpenInNew, Close, Timelapse, CheckCircle, Error, MoreVert as MoreVertIcon } from '@mui/icons-material_modern'

import {
  useGetDispatchTicketQuery,
  usePatchDispatchTicketMutation,
  useVoidDispatchTicketMutation,
} from 'services/dispatch'
import { useGetDriversQuery } from 'services/drivers'
import { showFormModal } from 'state/ui'

import Input from 'components/generic/form/Input'
import DatePicker from 'components/generic/form/DatePicker'
import TimePicker from 'components/generic/form/TimePicker'
import Loading from 'components/generic/Loading'
import PrintDialog from 'components/generic/printable/PrintDialog'
import PrintableDispatchTicket from 'components/generic/printable/PrintableDispatchTicket'
import PrintableMiniDispatch from 'components/generic/printable/PrintableMiniDispatch'

import {
  ASSET_CATEGORY,
  DISPATCH_TICKET_STATUSES,
  DISPATCH_TICKET_TYPES,
  DT_STATUS_ENUM,
  GS_ERROR_CODE,
} from 'utils/constants'
import { combineDateAndTime } from 'components/containers/load-board/utils/helperFunctions'
import {
  DISPATCH_STATUS_ICONS_DICT,
  defaultTicketPreviewModalValues,
  inputPropsStandard,
  inputPropsFilled,
} from 'components/containers/load-board/utils/constants'
import {
  dateFormatter,
  formatFullAddress,
  formatPhoneNumber,
  timeFormatter,
} from 'utils/data-manipulation/valueFormatter'
import FORM_MODALS from 'utils/ui/modals'
import { dispatchSnackbar } from 'utils/helpers'
import { Menu, MenuItem } from '@mui/material'

// TODO: Refactor to remove duplicate code
// TODO: Add "Reschedule" functionality
const DispatchTicketPreviewModal = ({ onFormStateChange, onHideModal }) => {
  const { yardDetailsList } = useSelector(state => state.auth)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { ticketDataFromList } = useSelector(state => state.ui.formModalProps)
  const { id: dispatchTicketId } = ticketDataFromList || {}
  const { setValue, control, getValues, watch } = useForm({ defaultValues: defaultTicketPreviewModalValues })
  const [statusIsChanging, setStatusIsChanging] = useState(false)
  const [openPrintDialog, setOpenPrintDialog] = useState(false)
  const [openMiniPrintDialog, setOpenMiniPrintDialog] = useState(false)
  const curDriver = watch('driver')

  const { data: dispatchTicketData } = useGetDispatchTicketQuery({ dispatchTicketId }, { skip: !dispatchTicketId })
  const { data: driversData } = useGetDriversQuery({
    filter: JSON.stringify({ isActive: true }),
  })
  const [patchDispatchTicket] = usePatchDispatchTicketMutation()

  const [actionsAnchorEl, setActionsAnchorEl] = useState(null)

  const handleOpenActions = event => {
    event.stopPropagation()
    setActionsAnchorEl(event.currentTarget)
  }

  const handleCloseActions = () => {
    setActionsAnchorEl(null)
  }

  const onDuplicateDispatchTicket = () => {
    navigate('/app/load-board/ticket-history/new', { state: { duplicateData: dispatchTicketData } })
    handleCloseActions()
  }

  const dispatchTicketStatus = dispatchTicketData?.status || ticketDataFromList?.status
  const assetIds = useMemo(
    () => dispatchTicketData?.dispatchTicketAssets?.map(asset => ({ id: asset.assetId || asset.id })) || [],
    [dispatchTicketData],
  )

  const assetLabel = useMemo(() => {
    const type = ticketDataFromList?.type || dispatchTicketData?.type
    if (type === DISPATCH_TICKET_TYPES.EXCHANGE.id) {
      return 'Pickup Assets'
    } else {
      return `${DISPATCH_TICKET_TYPES[dispatchTicketData?.type || ticketDataFromList?.type]?.name} Assets`
    }
  }, [dispatchTicketData, ticketDataFromList])

  const checkType = (ticketDataFromList?.type || dispatchTicketData?.type) === DISPATCH_TICKET_TYPES.EXCHANGE.id

  const locationContact = useMemo(() => {
    // If a specific contact is set, use that. Otherwise, use the primary contact of the customer
    if (dispatchTicketData?.contact) {
      return dispatchTicketData.contact
    } else if (dispatchTicketData?.customer?.contacts?.length > 0) {
      const locationId = dispatchTicketData.location?.id
      return (
        dispatchTicketData.customer.contacts.find(
          contact => contact.locationId === locationId && contact.isPrimaryContact,
        ) ||
        dispatchTicketData.customer.contacts.find(contact => contact.locationId === locationId) ||
        dispatchTicketData.customer.contacts.find(contact => contact.isPrimaryContact) ||
        dispatchTicketData.customer.contacts[0]
      )
    } else {
      return null
    }
  }, [dispatchTicketData])

  const locationAddress = useMemo(() => {
    if (dispatchTicketData?.location?.address1) {
      return formatFullAddress({
        address: dispatchTicketData.location.address1,
        city: dispatchTicketData.location.city,
        state: dispatchTicketData.location.state,
      })
    } else if (dispatchTicketData?.location?.address2) {
      return formatFullAddress({
        address: dispatchTicketData.location.address2,
        city: dispatchTicketData.location.city,
        state: dispatchTicketData.location.state,
      })
    }
    return ''
  }, [dispatchTicketData])

  const customerNumber = useMemo(() => {
    if (locationContact?.phone1) {
      return formatPhoneNumber(locationContact?.phone1.replace(/\D/g, ''))
    } else if (locationContact?.phone2) {
      return formatPhoneNumber(locationContact?.phone1.replace(/\D/g, ''))
    }
    return ''
  }, [dispatchTicketData, locationContact])

  useEffect(() => {
    onFormStateChange(false)
  }, [])

  const { pickupAssets, dropoffAssets } = useMemo(() => {
    const dtData = dispatchTicketData || ticketDataFromList

    const newPickupAssets = []
    const newDropoffAssets = []
    if (ticketDataFromList) {
      const { locationId: customerLocationId } = dtData
      dtData?.dispatchTicketAssets?.forEach(asset => {
        // if "to location" is the customer, then it is a dropoff asset
        // if "from location" is the customer, then it is a pickup asset
        if (asset.asset.assetCategory === ASSET_CATEGORY.CONTAINER) {
          if (asset.toLocationId === customerLocationId) {
            newDropoffAssets.push(asset)
          } else {
            newPickupAssets.push(asset)
          }
        }
      })
    }

    return {
      pickupAssets: newPickupAssets,
      dropoffAssets: newDropoffAssets,
    }
  }, [ticketDataFromList, dispatchTicketData])

  useEffect(() => {
    setValue('type', DISPATCH_TICKET_TYPES[dispatchTicketData?.type || ticketDataFromList?.type]?.name)
    setValue('customerName', ticketDataFromList?.customer?.displayName)
    setValue('customerLocation', ticketDataFromList?.location?.name)
    const selectedDriver = driversData?.Items?.find(
      driver => driver.id === (dispatchTicketData ? dispatchTicketData.driverId : ticketDataFromList?.driverId),
    )
    setValue('driver', selectedDriver || null)
    setValue('driverName', selectedDriver?.name || '')
    setValue('completionNote', dispatchTicketData?.dispatchTicketAssets?.[0]?.completionNote || '')
    const scheduleDate = new Date(
      dispatchTicketData ? dispatchTicketData.scheduleDate : ticketDataFromList?.scheduleDate,
    )
    const startDate = new Date(dispatchTicketData ? dispatchTicketData.startTime : ticketDataFromList?.startTime)
    const completionDate = new Date(
      dispatchTicketData ? dispatchTicketData.completionTime : ticketDataFromList?.completionTime,
    )
    setValue('requestDate', dispatchTicketData?.requestDate)
    setValue('requestedScheduleDate', dispatchTicketData?.requestedScheduleDate)
    setValue('requestNotes', dispatchTicketData?.requestNotes)
    setValue('scheduleDate', scheduleDate)
    setValue(
      'scheduleDateString',
      dateFormatter(scheduleDate, null, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    )
    setValue('scheduleTime', dispatchTicketData?.scheduleTimeActive ? scheduleDate : null)
    setValue('scheduleTimeString', dispatchTicketData?.scheduleTimeActive ? timeFormatter(scheduleDate) : '')
    setValue('startDate', startDate)
    setValue('startTime', startDate)
    setValue('startTimeString', timeFormatter(startDate))
    setValue('completionTimeString', !isNaN(completionDate.getTime()) ? timeFormatter(completionDate) : '')
    setValue('customerPhone', customerNumber)
    setValue('locationAddress', locationAddress)
    if (ticketDataFromList?.type !== DISPATCH_TICKET_TYPES.EXCHANGE.id) {
      if (ticketDataFromList?.locationId == pickupAssets[0]?.fromLocationId) {
        setValue(
          'assetName',
          pickupAssets.map(asset => `${asset.asset?.assetName} (${asset.asset?.type})`).join('\n') || '',
        )
      } else {
        setValue(
          'assetName',
          dropoffAssets.map(asset => `${asset.asset?.assetName} (${asset.asset?.type})`).join('\n') || '',
        )
      }
    } else if (ticketDataFromList?.type === DISPATCH_TICKET_TYPES.EXCHANGE.id) {
      setValue(
        'assetName',
        pickupAssets.map(asset => `${asset.asset?.assetName} (${asset.asset?.type})`).join('\n') || '',
      )
      setValue(
        'exchangeDropoffAssetName',
        dropoffAssets.map(asset => `${asset.asset?.assetName} (${asset.asset?.type})`).join('\n') || '',
      )
    }
    const truck = (dispatchTicketData || ticketDataFromList)?.dispatchTicketAssets?.find(
      assetStatus => assetStatus.asset?.assetCategory === ASSET_CATEGORY.TRUCK,
    )
    const trailer = (dispatchTicketData || ticketDataFromList)?.dispatchTicketAssets?.find(
      assetStatus => assetStatus.asset?.assetCategory === ASSET_CATEGORY.TRAILER,
    )
    setValue('truckName', truck ? `${truck?.asset?.assetName} (${truck?.asset?.type})` : '')
    setValue('trailerName', trailer ? `${trailer?.asset?.assetName} (${trailer?.asset?.type})` : '')
  }, [pickupAssets, dropoffAssets, ticketDataFromList, dispatchTicketData])

  const [voidDispatchTicket, { isLoading: isVoidingDispatchTicket }] = useVoidDispatchTicketMutation()

  const handlePatchError = error => {
    let errorMessage = `There was an error updating the ticket.`

    if (error?.data?.code === GS_ERROR_CODE.CONCURRENCY_VIOLATION) {
      errorMessage = `Dispatch ticket has been updated by another user. Please refresh your page and try again.`
    }

    dispatchSnackbar(dispatch, 'error', errorMessage)
  }

  const onClickVoidDispatchTicket = async () => {
    if (!dispatchTicketData) {
      return
    }

    if (dispatchTicketData.status === DISPATCH_TICKET_STATUSES.VOID.id) {
      dispatchSnackbar(dispatch, 'error', 'This dispatch ticket is already marked as VOID')
      return null
    }

    if (dispatchTicketData.id) {
      await voidDispatchTicket({
        voidDispatchTicketBody: dispatchTicketData,
        dispatchTicketId: dispatchTicketData.id,
      })
        .unwrap()
        .then(() => {
          dispatchSnackbar(dispatch, 'success', 'Dispatch Ticket Voided')
          navigate('/app/load-board/active-tickets')
        })
        .catch(error => {
          let errorMessage = `An error occurred voiding the dispatch ticket.`

          if (error?.data?.code === GS_ERROR_CODE.CONCURRENCY_VIOLATION) {
            errorMessage = `Dispatch ticket has been updated by another user. Please refresh your page and try again.`
          }

          dispatchSnackbar(dispatch, 'error', errorMessage)
        })
    }
    onHideModal()
    return null
  }

  // TODO: Refactor this function to reduce duplicate code
  const generateContent = useCallback(
    status => {
      switch (status) {
        case DISPATCH_TICKET_STATUSES.REQUESTED.id:
          return (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1}>
                <DatePicker
                  name="requestDate"
                  label="Request Date"
                  defaultDate={dispatchTicketData?.requestDate ? new Date(dispatchTicketData?.requestDate) : undefined}
                  control={control}
                  datePickerProps={{
                    disabled: true,
                  }}
                />
                <DatePicker
                  name="requestedScheduleDate"
                  label="Proposed Date"
                  defaultDate={
                    dispatchTicketData?.requestedScheduleDate
                      ? new Date(dispatchTicketData?.requestedScheduleDate)
                      : undefined
                  }
                  control={control}
                  datePickerProps={{
                    disabled: true,
                  }}
                  x
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <DatePicker
                  name="scheduleDate"
                  label="Schedule Date"
                  defaultDate={
                    dispatchTicketData?.scheduleDate ? new Date(dispatchTicketData?.scheduleDate) : undefined
                  }
                  control={control}
                  datePickerProps={{
                    onChange: async date => {
                      setValue('scheduleDate', date)
                      if (!date || Number.isNaN(date.getTime())) return // don't fire patch if input is invalid

                      await patchDispatchTicket({
                        patchDispatchTicketBody: {
                          id: dispatchTicketId,
                          scheduleDate: date.toISOString(),
                          status: curDriver
                            ? DISPATCH_TICKET_STATUSES.ASSIGNED.id
                            : DISPATCH_TICKET_STATUSES.UNASSIGNED.id,
                          hash: dispatchTicketData?.hash,
                        },
                        dispatchTicketId,
                        assetIds,
                      }).catch(handlePatchError)
                    },
                  }}
                />
              </Stack>
              <Stack direction="row">
                <Input
                  name="requestNotes"
                  label="Request Note"
                  defaultValue={dispatchTicketData?.requestNotes || ''}
                  control={control}
                  {...inputPropsStandard}
                />
              </Stack>
              <Stack spacing={0.5} width="100%">
                <Stack direction="row">
                  <Input name="type" label="Type" defaultValue="" control={control} {...inputPropsStandard} />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="customerName"
                    label="Customer Name"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="customerLocation"
                    label="Customer Location"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="customerPhone"
                    label="Contact Number"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="locationAddress"
                    label="Address"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
              </Stack>
            </Stack>
          )
        case DISPATCH_TICKET_STATUSES.IN_PROGRESS.id:
          return (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1}>
                <DatePicker
                  name="startDate"
                  label="Start Date"
                  defaultDate={null}
                  control={control}
                  datePickerProps={{
                    onChange: async date => {
                      setValue('startDate', date)
                      if (!date || Number.isNaN(date.getTime())) return // don't fire patch if input is invalid

                      const time = getValues('startTime')
                      const timeIsInvalid = !time || Number.isNaN(time.getTime())
                      const now = new Date()
                      if (timeIsInvalid) {
                        setValue('startTime', now) // if time is invalid, use now
                      }

                      await patchDispatchTicket({
                        patchDispatchTicketBody: {
                          id: dispatchTicketId,
                          startTime: combineDateAndTime(date, timeIsInvalid ? now : time),
                          hash: dispatchTicketData?.hash,
                        },
                        dispatchTicketId,
                        assetIds,
                      }).catch(handlePatchError)
                    },
                  }}
                  {...inputPropsFilled}
                />
                <TimePicker
                  name="startTime"
                  label="Start Time"
                  defaultTime={null}
                  control={control}
                  timePickerProps={{
                    onChange: async time => {
                      setValue('startTime', time)
                      if (!time || Number.isNaN(time.getTime())) return // don't fire patch if input is invalid

                      const date = getValues('startDate')
                      const dateIsInvalid = !date || Number.isNaN(date.getTime())
                      const now = new Date()
                      if (dateIsInvalid) {
                        setValue('startDate', now) // if date is invalid, use now
                      }

                      await patchDispatchTicket({
                        patchDispatchTicketBody: {
                          id: dispatchTicketId,
                          startTime: combineDateAndTime(dateIsInvalid ? now : date, time),
                          hash: dispatchTicketData?.hash,
                        },
                        dispatchTicketId,
                        assetIds,
                      }).catch(handlePatchError)
                    },
                  }}
                  {...inputPropsFilled}
                />
              </Stack>
              <Stack spacing={0.5} width="100%">
                <Stack direction="row">
                  <Input name="driverName" label="Driver" defaultValue="" control={control} {...inputPropsStandard} />
                  <Input name="type" label="Type" defaultValue="" control={control} {...inputPropsStandard} />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="scheduleDateString"
                    label="Scheduled Date"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="scheduleTimeString"
                    label="Scheduled Time"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="customerName"
                    label="Customer Name"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="customerLocation"
                    label="Customer Location"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="customerPhone"
                    label="Contact Number"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="locationAddress"
                    label="Address"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="assetName"
                    label={assetLabel}
                    defaultValue=""
                    control={control}
                    multiline
                    {...inputPropsStandard}
                  />
                  {checkType && (
                    <Input
                      name="exchangeDropoffAssetName"
                      label="Dropoff Assets"
                      defaultValue=""
                      control={control}
                      multiline
                      {...inputPropsStandard}
                    />
                  )}
                </Stack>
                <Stack direction="row">
                  <Input name="truckName" label="Truck" defaultValue="" control={control} {...inputPropsStandard} />
                  <Input name="trailerName" label="Trailer" defaultValue="" control={control} {...inputPropsStandard} />
                </Stack>
              </Stack>
            </Stack>
          )
        case DISPATCH_TICKET_STATUSES.COMPLETED.id:
          return (
            <Stack spacing={1}>
              <Stack spacing={1} width="100%">
                {dispatchTicketData ? (
                  <>
                    {dropoffAssets && dropoffAssets.length > 0 && (
                      <TextField
                        key={'completedDropoffAssets'}
                        label="Dropoff Assets"
                        {...inputPropsStandard}
                        sx={{
                          ...inputPropsStandard.sx,
                          backgroundColor: 'rgba(76, 175, 80, 0.1)',
                          borderRadius: '2px',
                        }}
                        value={
                          dropoffAssets.map(asset => `${asset.asset?.assetName} (${asset.asset?.type})`).join('\n') ||
                          ''
                        }
                        multiline
                      />
                    )}
                    {pickupAssets && pickupAssets.length > 0 && (
                      <TextField
                        key={'completedPickupAssets'}
                        label={assetLabel}
                        {...inputPropsStandard}
                        sx={{
                          ...inputPropsStandard.sx,
                          backgroundColor: 'rgba(76, 175, 80, 0.1)',
                          borderRadius: '2px',
                        }}
                        value={
                          pickupAssets.map(asset => `${asset.asset?.assetName} (${asset.asset?.type})`).join('\n') || ''
                        }
                        multiline
                      />
                    )}
                  </>
                ) : (
                  <Loading localized localizedSx={{ height: '50px' }} />
                )}
              </Stack>
              <Stack spacing={0.5} width="100%">
                <Input
                  name="completionNote"
                  label="Completed Ticket Notes"
                  defaultValue=""
                  control={control}
                  {...inputPropsStandard}
                />
                <Stack direction="row">
                  <Input name="driverName" label="Driver" defaultValue="" control={control} {...inputPropsStandard} />
                  <Input name="type" label="Type" defaultValue="" control={control} {...inputPropsStandard} />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="startTimeString"
                    label="Departed"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="completionTimeString"
                    label="Arrived"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="scheduleDateString"
                    label="Scheduled Date"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="scheduleTimeString"
                    label="Scheduled Time"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="customerName"
                    label="Customer Name"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="customerLocation"
                    label="Customer Location"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="customerPhone"
                    label="Contact Number"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="locationAddress"
                    label="Address"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input name="truckName" label="Truck" defaultValue="" control={control} {...inputPropsStandard} />
                  <Input name="trailerName" label="Trailer" defaultValue="" control={control} {...inputPropsStandard} />
                </Stack>
              </Stack>
            </Stack>
          )
        case DISPATCH_TICKET_STATUSES.FAILED.id:
          return (
            <Stack spacing={1}>
              <Stack spacing={1} width="100%">
                {dispatchTicketData ? (
                  <>
                    {dropoffAssets && dropoffAssets.length > 0 && (
                      <TextField
                        key={'failedDropoffAssets'}
                        label="Dropoff Assets"
                        {...inputPropsStandard}
                        sx={{
                          ...inputPropsStandard.sx,
                          backgroundColor: 'rgba(244, 67, 54, 0.1)',
                          borderRadius: '2px',
                        }}
                        value={
                          dropoffAssets.map(asset => `${asset.asset?.assetName} (${asset.asset?.type})`).join('\n') ||
                          ''
                        }
                        multiline
                      />
                    )}
                    {pickupAssets && pickupAssets.length > 0 && (
                      <TextField
                        key={'failedPickupAssets'}
                        label={assetLabel}
                        {...inputPropsStandard}
                        sx={{
                          ...inputPropsStandard.sx,
                          backgroundColor: 'rgba(244, 67, 54, 0.1)',
                          borderRadius: '2px',
                        }}
                        value={
                          pickupAssets.map(asset => `${asset.asset?.assetName} (${asset.asset?.type})`).join('\n') || ''
                        }
                        multiline
                      />
                    )}
                  </>
                ) : (
                  <Loading localized localizedSx={{ height: '50px' }} />
                )}
              </Stack>
              <Stack spacing={0.5} width="100%">
                <Input
                  name="completionNote"
                  label="Failed Ticket Notes"
                  defaultValue=""
                  control={control}
                  {...inputPropsStandard}
                />
                <Stack direction="row">
                  <Input name="driverName" label="Driver" defaultValue="" control={control} {...inputPropsStandard} />
                  <Input name="type" label="Type" defaultValue="" control={control} {...inputPropsStandard} />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="startTimeString"
                    label="Departed"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="completionTimeString"
                    label="Arrived"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="scheduleDateString"
                    label="Scheduled Date"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="scheduleTimeString"
                    label="Scheduled Time"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="customerName"
                    label="Customer Name"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="customerLocation"
                    label="Customer Location"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="customerPhone"
                    label="Contact Number"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="locationAddress"
                    label="Address"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input name="truckName" label="Truck" defaultValue="" control={control} {...inputPropsStandard} />
                  <Input name="trailerName" label="Trailer" defaultValue="" control={control} {...inputPropsStandard} />
                </Stack>
              </Stack>
            </Stack>
          )
        case DISPATCH_TICKET_STATUSES.ASSIGNED.id:
          return (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1}>
                <Controller
                  name="driver"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      getOptionLabel={option => option?.name}
                      options={driversData?.Items || []}
                      renderInput={params => (
                        <TextField
                          {...params}
                          {...inputPropsFilled}
                          InputProps={{ ...params.InputProps, ...inputPropsFilled.InputProps }}
                          label="Driver"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option?.id} style={{ width: '100%' }}>
                          {option?.name}
                        </li>
                      )}
                      sx={{ width: '100%' }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      onChange={async (_, driver) => {
                        setValue('driver', driver)
                        await patchDispatchTicket({
                          patchDispatchTicketBody: {
                            id: dispatchTicketId,
                            driverId: driver?.id || null,
                            status: (driver ? DISPATCH_TICKET_STATUSES.ASSIGNED : DISPATCH_TICKET_STATUSES.UNASSIGNED)
                              .id,
                            hash: dispatchTicketData?.hash,
                          },
                          dispatchTicketId,
                          assetIds,
                        }).catch(handlePatchError)
                      }}
                    />
                  )}
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <DatePicker
                  name="scheduleDate"
                  label="Scheduled Date"
                  control={control}
                  defaultDate={null}
                  datePickerProps={{
                    onChange: async date => {
                      setValue('scheduleDate', date)
                      if (!date || Number.isNaN(date.getTime())) return // don't fire patch if input is invalid

                      const time = getValues('scheduleTime')
                      const timeIsInvalid = !time || Number.isNaN(time.getTime())
                      const now = new Date()

                      await patchDispatchTicket({
                        patchDispatchTicketBody: {
                          id: dispatchTicketId,
                          scheduleDate: combineDateAndTime(date, timeIsInvalid ? now : time),
                          scheduleTimeActive: !timeIsInvalid,
                          hash: dispatchTicketData?.hash,
                        },
                        dispatchTicketId,
                        assetIds,
                      }).catch(handlePatchError)
                    },
                  }}
                  {...inputPropsFilled}
                />
                <TimePicker
                  name="scheduleTime"
                  label="Scheduled Time"
                  control={control}
                  defaultTime={null}
                  timePickerProps={{
                    onChange: async time => {
                      setValue('scheduleTime', time)
                      if (!time || Number.isNaN(time.getTime())) return // don't fire patch if input is invalid

                      const date = getValues('scheduleDate')
                      const dateIsInvalid = !date || Number.isNaN(date.getTime())
                      const now = new Date()
                      if (dateIsInvalid) {
                        setValue('scheduleDate', now) // if date is invalid, use now
                      }

                      await patchDispatchTicket({
                        patchDispatchTicketBody: {
                          id: dispatchTicketId,
                          scheduleDate: combineDateAndTime(dateIsInvalid ? now : date, time),
                          scheduleTimeActive: true,
                          hash: dispatchTicketData?.hash,
                        },
                        dispatchTicketId,
                        assetIds,
                      }).catch(handlePatchError)
                    },
                  }}
                  {...inputPropsFilled}
                />
              </Stack>
              <Stack spacing={0.5} width="100%">
                <Stack direction="row">
                  <Input name="type" label="Type" defaultValue="" control={control} {...inputPropsStandard} />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="customerName"
                    label="Customer Name"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="customerLocation"
                    label="Customer Location"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="customerPhone"
                    label="Contact Number"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="locationAddress"
                    label="Address"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="assetName"
                    label={assetLabel}
                    defaultValue=""
                    control={control}
                    multiline
                    {...inputPropsStandard}
                  />
                  {checkType && (
                    <Input
                      name="exchangeDropoffAssetName"
                      label="Dropoff Asset"
                      defaultValue=""
                      control={control}
                      multiline
                      {...inputPropsStandard}
                    />
                  )}
                </Stack>
                <Stack direction="row">
                  <Input name="truckName" label="Truck" defaultValue="" control={control} {...inputPropsStandard} />
                  <Input name="trailerName" label="Trailer" defaultValue="" control={control} {...inputPropsStandard} />
                </Stack>
              </Stack>
            </Stack>
          )
        case DISPATCH_TICKET_STATUSES.UNASSIGNED.id:
          return (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1}>
                <Controller
                  name="driver"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      getOptionLabel={option => option?.name}
                      options={driversData?.Items || []}
                      renderInput={params => (
                        <TextField
                          {...params}
                          {...inputPropsFilled}
                          InputProps={{ ...params.InputProps, ...inputPropsFilled.InputProps }}
                          label="Driver"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option?.id} style={{ width: '100%' }}>
                          {option?.name}
                        </li>
                      )}
                      sx={{ width: '100%' }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      onChange={async (_, driver) => {
                        setValue('driver', driver)
                        await patchDispatchTicket({
                          patchDispatchTicketBody: {
                            id: dispatchTicketId,
                            driverId: driver?.id || null,
                            status: (driver ? DISPATCH_TICKET_STATUSES.ASSIGNED : DISPATCH_TICKET_STATUSES.UNASSIGNED)
                              .id,
                            hash: dispatchTicketData?.hash,
                          },
                          dispatchTicketId,
                          assetIds,
                        }).catch(handlePatchError)
                      }}
                    />
                  )}
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <DatePicker
                  name="scheduleDate"
                  label="Scheduled Date"
                  control={control}
                  defaultDate={null}
                  datePickerProps={{
                    onChange: async date => {
                      setValue('scheduleDate', date)
                      if (!date || Number.isNaN(date.getTime())) return // don't fire patch if input is invalid

                      const time = getValues('scheduleTime')
                      const timeIsInvalid = !time || Number.isNaN(time.getTime())
                      const now = new Date()

                      await patchDispatchTicket({
                        patchDispatchTicketBody: {
                          id: dispatchTicketId,
                          scheduleDate: combineDateAndTime(date, timeIsInvalid ? now : time),
                          scheduleTimeActive: !timeIsInvalid,
                          hash: dispatchTicketData?.hash,
                        },
                        dispatchTicketId,
                        assetIds,
                      }).catch(handlePatchError)
                    },
                  }}
                  {...inputPropsFilled}
                />
                <TimePicker
                  name="scheduleTime"
                  label="Scheduled Time"
                  control={control}
                  defaultTime={null}
                  timePickerProps={{
                    onChange: async time => {
                      setValue('scheduleTime', time)
                      if (!time || Number.isNaN(time.getTime())) return // don't fire patch if input is invalid

                      const date = getValues('scheduleDate')
                      const dateIsInvalid = !date || Number.isNaN(date.getTime())
                      const now = new Date()
                      if (dateIsInvalid) {
                        setValue('scheduleDate', now) // if date is invalid, use now
                      }

                      await patchDispatchTicket({
                        patchDispatchTicketBody: {
                          id: dispatchTicketId,
                          scheduleDate: combineDateAndTime(dateIsInvalid ? now : date, time),
                          scheduleTimeActive: true,
                          hash: dispatchTicketData?.hash,
                        },
                        dispatchTicketId,
                        assetIds,
                      }).catch(handlePatchError)
                    },
                  }}
                  {...inputPropsFilled}
                />
              </Stack>
              <Stack spacing={0.5} width="100%">
                <Stack direction="row">
                  <Input name="type" label="Type" defaultValue="" control={control} {...inputPropsStandard} />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="customerName"
                    label="Customer Name"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="customerLocation"
                    label="Customer Location"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="customerPhone"
                    label="Contact Number"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="locationAddress"
                    label="Address"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="assetName"
                    label={assetLabel}
                    defaultValue=""
                    control={control}
                    multiline
                    {...inputPropsStandard}
                  />
                  {checkType && (
                    <Input
                      name="exchangeDropoffAssetName"
                      label="Dropoff Asset"
                      defaultValue=""
                      control={control}
                      multiline
                      {...inputPropsStandard}
                    />
                  )}
                </Stack>
                <Stack direction="row">
                  <Input name="truckName" label="Truck" defaultValue="" control={control} {...inputPropsStandard} />
                  <Input name="trailerName" label="Trailer" defaultValue="" control={control} {...inputPropsStandard} />
                </Stack>
              </Stack>
            </Stack>
          )
        case DISPATCH_TICKET_STATUSES.VOID.id:
          return (
            <Stack spacing={1}>
              <Stack spacing={1} width="100%">
                {dispatchTicketData ? (
                  <>
                    {dropoffAssets && dropoffAssets.length > 0 && (
                      <TextField
                        key={'voidDropoffAssets'}
                        label="Dropoff Assets"
                        {...inputPropsStandard}
                        sx={{
                          ...inputPropsStandard.sx,
                          backgroundColor: 'rgba(244, 67, 54, 0.1)',
                          borderRadius: '2px',
                        }}
                        value={
                          dropoffAssets.map(asset => `${asset.asset?.assetName} (${asset.asset?.type})`).join('\n') ||
                          ''
                        }
                        multiline
                      />
                    )}
                    {pickupAssets && pickupAssets.length > 0 && (
                      <TextField
                        key={'voidPickupAssets'}
                        label={assetLabel}
                        {...inputPropsStandard}
                        sx={{
                          ...inputPropsStandard.sx,
                          backgroundColor: 'rgba(244, 67, 54, 0.1)',
                          borderRadius: '2px',
                        }}
                        value={
                          pickupAssets.map(asset => `${asset.asset?.assetName} (${asset.asset?.type})`).join('\n') || ''
                        }
                        multiline
                      />
                    )}
                  </>
                ) : (
                  <Loading localized localizedSx={{ height: '50px' }} />
                )}
              </Stack>
              <Stack spacing={0.5} width="100%">
                <Input
                  name="completionNote"
                  label="Void Ticket Notes"
                  defaultValue=""
                  control={control}
                  {...inputPropsStandard}
                />
                <Stack direction="row">
                  <Input name="driverName" label="Driver" defaultValue="" control={control} {...inputPropsStandard} />
                  <Input name="type" label="Type" defaultValue="" control={control} {...inputPropsStandard} />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="startTimeString"
                    label="Departed"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="completionTimeString"
                    label="Arrived"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="scheduleDateString"
                    label="Scheduled Date"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="scheduleTimeString"
                    label="Scheduled Time"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="customerName"
                    label="Customer Name"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="customerLocation"
                    label="Customer Location"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input
                    name="customerPhone"
                    label="Contact Number"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                  <Input
                    name="locationAddress"
                    label="Address"
                    defaultValue=""
                    control={control}
                    {...inputPropsStandard}
                  />
                </Stack>
                <Stack direction="row">
                  <Input name="truckName" label="Truck" defaultValue="" control={control} {...inputPropsStandard} />
                  <Input name="trailerName" label="Trailer" defaultValue="" control={control} {...inputPropsStandard} />
                </Stack>
              </Stack>
            </Stack>
          )
        default:
          return null
      }
    },
    [pickupAssets, dropoffAssets, driversData],
  )

  // TODO: Refactor this function to reduce duplicate code
  const generateFooter = useCallback(
    status => {
      switch (status) {
        case DISPATCH_TICKET_STATUSES.REQUESTED.id:
          return <></>
        case DISPATCH_TICKET_STATUSES.IN_PROGRESS.id:
          return (
            <>
              <Divider />
              <Stack direction="row" spacing={2} pt={2} alignItems="center" justifyContent="center">
                <Typography variant="body2">Mark as:</Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<CheckCircle />}
                  onClick={() => {
                    setStatusIsChanging(true)
                    onHideModal({ skipBlocker: true })
                    dispatch(
                      showFormModal({
                        formModalName: FORM_MODALS.DISPATCH_TICKET_STATUS_BLADE,
                        formModalProps: {
                          type: DISPATCH_TICKET_STATUSES.COMPLETED.id,
                          dispatchTicketId,
                          startTime: dispatchTicketData?.startTime || ticketDataFromList?.startTime,
                        },
                      }),
                    )
                  }}
                >
                  COMPLETE
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Error />}
                  onClick={() => {
                    setStatusIsChanging(true)
                    onHideModal({ skipBlocker: true })
                    dispatch(
                      showFormModal({
                        formModalName: FORM_MODALS.DISPATCH_TICKET_STATUS_BLADE,
                        formModalProps: {
                          type: DISPATCH_TICKET_STATUSES.FAILED.id,
                          dispatchTicketId,
                          startTime: dispatchTicketData?.startTime || ticketDataFromList?.startTime,
                        },
                      }),
                    )
                  }}
                >
                  FAILED
                </Button>
              </Stack>
            </>
          )
        case DISPATCH_TICKET_STATUSES.COMPLETED.id:
          return null
        case DISPATCH_TICKET_STATUSES.FAILED.id:
          // Scoped out of V1
          // TODO: Implement "Reschedule" functionality
          //
          // return (
          //   <>
          //     <Divider />
          //     <Stack direction="row" spacing={2} pt={2} alignItems="center" justifyContent="center">
          //       <Button
          //         variant="text"
          //       >
          //         RESCHEDULE
          //       </Button>
          //     </Stack>
          //   </>
          // )
          return null
        case DISPATCH_TICKET_STATUSES.ASSIGNED.id:
        case DISPATCH_TICKET_STATUSES.UNASSIGNED.id:
          return (
            <>
              <Divider />
              <Stack direction="row" spacing={2} pt={2} alignItems="center" justifyContent="center">
                <Typography variant="body2">Mark as:</Typography>
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<Timelapse />}
                  onClick={async () => {
                    setStatusIsChanging(true)
                    await patchDispatchTicket({
                      patchDispatchTicketBody: {
                        id: dispatchTicketId,
                        startTime: new Date().toISOString(),
                        status: DISPATCH_TICKET_STATUSES.IN_PROGRESS.id,
                        hash: dispatchTicketData?.hash,
                      },
                      dispatchTicketId,
                      assetIds,
                    })
                      .catch(handlePatchError)
                      .finally(() => setStatusIsChanging(false))
                  }}
                >
                  IN PROGRESS
                </Button>
              </Stack>
            </>
          )
        default:
          return null
      }
    },
    [dispatchTicketData, ticketDataFromList, curDriver],
  )

  if (statusIsChanging) return <Loading localized />

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        <>
          <IconButton sx={{ padding: 1 }} onClick={handleOpenActions}>
            <MoreVertIcon />
          </IconButton>
          <Menu anchorEl={actionsAnchorEl} open={Boolean(actionsAnchorEl)} onClose={handleCloseActions}>
            <MenuItem
              onClick={onDuplicateDispatchTicket}
              disabled={dispatchTicketData?.status === DT_STATUS_ENUM.REQUESTED}
            >
              Duplicate Dispatch Ticket
            </MenuItem>
            <MenuItem
              onClick={() => setOpenMiniPrintDialog(true)}
              disabled={dispatchTicketData?.status === DT_STATUS_ENUM.REQUESTED}
            >
              Print Mini Tags
            </MenuItem>
            <MenuItem
              onClick={() => setOpenPrintDialog(true)}
              disabled={dispatchTicketData?.status === DT_STATUS_ENUM.REQUESTED}
            >
              Print Dispatch Ticket
            </MenuItem>
            <MenuItem
              onClick={onClickVoidDispatchTicket}
              disabled={[
                DISPATCH_TICKET_STATUSES.FAILED,
                DISPATCH_TICKET_STATUSES.COMPLETED,
                DISPATCH_TICKET_STATUSES.VOID,
              ].some(({ id }) => dispatchTicketData?.status === id)}
            >
              Void Dispatch Ticket
            </MenuItem>
          </Menu>
        </>
        {dispatchTicketData?.status !== DT_STATUS_ENUM.REQUESTED && (
          <Link to={`/app/load-board/ticket-history/${dispatchTicketId}`} target="_blank">
            <IconButton sx={{ padding: 1 }} onClick={() => {}}>
              <OpenInNew />
            </IconButton>
          </Link>
        )}
        <IconButton sx={{ padding: 1 }} onClick={() => onHideModal()}>
          <Close />
        </IconButton>
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h7">{`Ticket #${ticketDataFromList?.externalId || ''}`}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {DISPATCH_STATUS_ICONS_DICT[dispatchTicketStatus]?.previewModal || null}
          <Typography variant="body2">{DISPATCH_TICKET_STATUSES[dispatchTicketStatus]?.name}</Typography>
        </Stack>
      </Stack>
      <Stack direction="row">{generateContent(dispatchTicketStatus)}</Stack>
      <Stack>{generateFooter(dispatchTicketStatus)}</Stack>

      {openPrintDialog && (
        <PrintDialog
          title={`Dispatch Ticket No. ${dispatchTicketData?.externalId}`}
          open={openPrintDialog}
          onClose={() => setOpenPrintDialog(false)}
          showLogoAddress
        >
          <PrintableDispatchTicket
            customerData={dispatchTicketData?.customer}
            locationData={dispatchTicketData?.location}
            dispatchTicketData={dispatchTicketData}
            pickupAssets={pickupAssets}
            dropoffAssets={dropoffAssets}
          />
        </PrintDialog>
      )}

      {openMiniPrintDialog && (
        <PrintDialog
          title={`Mini Tags - Dispatch Ticket No. ${dispatchTicketData?.externalId}`}
          open={openMiniPrintDialog}
          onClose={() => setOpenMiniPrintDialog(false)}
          showLogoAddress={false}
        >
          <PrintableMiniDispatch
            customerName={dispatchTicketData?.customer?.displayName || ''}
            dispatchNumber={dispatchTicketData?.externalId || ''}
            scheduleDate={dispatchTicketData?.scheduleDate || ''}
          />
        </PrintDialog>
      )}
    </Stack>
  )
}

DispatchTicketPreviewModal.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

DispatchTicketPreviewModal.defaultProps = {
  onFormStateChange: noop,
  onHideModal: null,
}

export default DispatchTicketPreviewModal
